export function getBuyerTypeAttrs(entity) {
  return entity?.attributes ?? entity ?? {}
}

function normalizeBuyerTypeKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
}

export function buyerTypeRefKeys(entity) {
  const attrs = getBuyerTypeAttrs(entity)
  return [
    entity?.id,
    entity?.documentId,
    attrs.id,
    attrs.documentId,
    attrs.abbreviation,
    attrs.name,
  ]
    .filter((value) => value != null && String(value).trim() !== '')
    .map(normalizeBuyerTypeKey)
}

export function isCompSystemDefault(entity) {
  const attrs = getBuyerTypeAttrs(entity)
  const name = normalizeBuyerTypeKey(attrs.name)
  const abbreviation = normalizeBuyerTypeKey(attrs.abbreviation)
  return name === 'comp' || abbreviation === 'comp'
}

export function compParentKeysFromDefaults(defaultEntities = []) {
  const keys = new Set()
  for (const entity of defaultEntities) {
    if (!isCompSystemDefault(entity)) continue
    for (const key of buyerTypeRefKeys(entity)) keys.add(key)
  }
  return keys
}

export function isComplimentaryBuyerType(entity, compParentKeys = new Set()) {
  const attrs = getBuyerTypeAttrs(entity)
  if (attrs.complimentary === true) return true
  if (attrs.category === 'comp' || attrs.category === 'box_office_comp') {
    return true
  }
  const parent = normalizeBuyerTypeKey(attrs.parent)
  if (!parent) return false
  if (parent === 'comp' || parent === 'complimentary') return true
  return compParentKeys.has(parent)
}

/** True if buyer type's sub_types includes the given channel key. */
export function buyerTypeHasChannelSubType(buyerTypeEntity, channelKey) {
  const attrs = getBuyerTypeAttrs(buyerTypeEntity)
  let arr = attrs?.sub_types
  if (typeof arr === 'string') {
    try {
      arr = JSON.parse(arr)
    } catch {
      return false
    }
  }
  if (!Array.isArray(arr)) return false
  return arr.some((item) => {
    if (item != null && typeof item === 'object') {
      const key = item.key ?? item.id ?? item.slug
      return String(key) === channelKey
    }
    return String(item) === channelKey
  })
}

export function buyerTypeHasTicketAssignmentSubType(buyerTypeEntity) {
  return buyerTypeHasChannelSubType(buyerTypeEntity, 'ticket_assignment')
}

const GROUP_ASSIGNMENT_SEAT_THRESHOLD = 20

function isSeasonTicketHolderRateBuyer(entity) {
  const attrs = getBuyerTypeAttrs(entity)
  const name = String(attrs.name || '').trim()
  const abbreviation = String(attrs.abbreviation || '').trim()
  return (
    /season ticket holder rate/i.test(name) ||
    /^sth\s*rate/i.test(name) ||
    /^sthrate/i.test(abbreviation)
  )
}

/** Group buyer types identify a group assignment even under 20 seats. */
export function buyerTypeAssignmentKind(entity) {
  if (!entity || isSeasonTicketHolderRateBuyer(entity)) return null
  const attrs = getBuyerTypeAttrs(entity)
  const category = normalizeBuyerTypeKey(attrs.category)
  if (category === 'group') return 'group'
  if (category === 'individual') return null
  const abbreviation = normalizeBuyerTypeKey(attrs.abbreviation)
  const name = normalizeBuyerTypeKey(attrs.name)
  if (
    abbreviation === 'g' ||
    abbreviation === 'group' ||
    name === 'group' ||
    name.startsWith('group ')
  ) {
    return 'group'
  }
  return null
}

/**
 * Paid assignment group vs individual.
 * A group buyer type counts as a group below the seat threshold.
 * Twenty or more seats still count as a group.
 */
export function resolvePaidAssignmentTicketType({
  seatCount = 0,
  buyerType,
} = {}) {
  if (buyerTypeAssignmentKind(buyerType) === 'group') return 'group'
  return Number(seatCount) >= GROUP_ASSIGNMENT_SEAT_THRESHOLD
    ? 'group'
    : 'individual'
}

const BUYER_TYPE_CATEGORIES = ['comp', 'individual', 'group', 'box_office_comp']

/** Category to persist when creating a custom type from a parent. */
export function inferBuyerTypeCategory(parentEntity) {
  const attrs = getBuyerTypeAttrs(parentEntity)
  if (BUYER_TYPE_CATEGORIES.includes(attrs.category)) return attrs.category
  const key = normalizeBuyerTypeKey(attrs.name || attrs.abbreviation)
  if (key === 'standard') return 'individual'
  if (key === 'comp' || key === 'complimentary') return 'comp'
  if (key === 'group') return 'group'
  if (key === 'box_office_comp' || key === 'box office comp') {
    return 'box_office_comp'
  }
  return undefined
}

export function parentIsComplimentary(parentEntity) {
  const attrs = getBuyerTypeAttrs(parentEntity)
  if (attrs.complimentary === true) return true
  if (attrs.category === 'comp' || attrs.category === 'box_office_comp') {
    return true
  }
  return isCompSystemDefault(parentEntity)
}

/** Only Comp uses the Comp type list. Reserved is a hold and uses paid buyer types. */
export function isAssignmentCompLabelMethod(paymentMethod) {
  return String(paymentMethod || '').toUpperCase() === 'COMP'
}

export function isAssignmentLabelSelectionValid({
  paymentMethod,
  compLabels = [],
  selectedCompLabel,
  buyerTypes = [],
  selectedBuyerType,
}) {
  if (isAssignmentCompLabelMethod(paymentMethod)) {
    return compLabels.length > 0 ? Boolean(selectedCompLabel) : true
  }
  return buyerTypes.length > 0 ? Boolean(selectedBuyerType) : true
}
